import styled from '@emotion/styled';
import Tippy, { type TippyProps } from '@tippyjs/react';
import { useRef } from 'react';
import type { Instance } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { useDevice } from '../../hooks/useDevice';
import { Typography } from '../typography';

const StyledTippy = styled(Tippy)`
  display: flex;
  min-width: 352px;
  max-width: 424px;
  padding: 12px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  background-color: ${({ theme }) =>
    theme.tokens.color.alias.cm.surface['surface-secondary'].value}!important;
  color: ${({ theme }) => theme.tokens.color.alias.cm.text.text.value};
  border-radius: 4px;
  box-shadow: ${({ theme }) =>
    `0px 2px 4px 0px ${theme.tokens.elevation['elevation 2'].value.color}`};
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  justify-content: space-between;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const StyledSpanBase = styled.span`
  display: inline-block;
`;

const StyledSpan = styled(StyledSpanBase)`
  width: auto;
  > * {
    height: 100%;
  }
`;

type TippyElement = Element & {
  _tippy: Instance;
};

type Props = {
  buttonText?: string;
  children: React.ReactNode;
  description: string;
  hasChildrenWithEllipsis?: boolean;
  headline?: string;
  subtitle?: string;
  onButtonClick?: () => void;
  trigger?: 'click' | 'hover';
} & TippyProps;

export function RichTooltip({
  buttonText,
  children,
  delay = [400, 100],
  description,
  disabled = false,
  hasChildrenWithEllipsis = false,
  headline,
  subtitle,
  onButtonClick,
  trigger,
  ...rest
}: Props): JSX.Element {
  const { isTouchable } = useDevice();
  const { theme, ...tippyProps } = rest;
  const tippyRef = useRef<TippyElement>(null);

  const handleButtonClick = (): void => {
    onButtonClick?.();
    tippyRef.current?._tippy?.hide();
  };

  const wrapChildren = (): JSX.Element => {
    if (hasChildrenWithEllipsis) {
      return <StyledSpanBase>{children}</StyledSpanBase>;
    }

    if (
      typeof children?.type === 'function' ||
      typeof children?.type === 'object'
    ) {
      return <StyledSpan className={rest.className}>{children}</StyledSpan>;
    }

    return children as JSX.Element;
  };

  const renderContent = (): JSX.Element => (
    <ContentContainer>
      {(headline || subtitle) && (
        <HeaderContainer>
          {headline && <Typography.Subtitle2>{headline}</Typography.Subtitle2>}
          {subtitle && <Typography.Caption>{subtitle}</Typography.Caption>}
        </HeaderContainer>
      )}
      <Typography.Body2>{description}</Typography.Body2>
      {buttonText && onButtonClick && (
        <Typography.Link3 onClick={handleButtonClick}>
          {buttonText}
        </Typography.Link3>
      )}
    </ContentContainer>
  );

  if (!description) {
    return <>{children}</>;
  }

  return (
    <StyledTippy
      ref={tippyRef}
      disabled={disabled}
      content={renderContent()}
      delay={delay}
      arrow={false}
      offset={[0, 4]}
      touch={isTouchable}
      interactive={true}
      trigger={isTouchable ? 'click' : trigger}
    >
      {wrapChildren()}
    </StyledTippy>
  );
}
